import React from 'react'
import { SizeRequirements, SVGDatumType } from '..'
import { MultiChartProps } from './typings'


const svgNS = 'http://www.w3.org/2000/svg'

export const getSVGTextWidth = (text: string): number => {
  const svgEl = document.createElementNS(svgNS, 'svg')
  svgEl.setAttributeNS(svgNS, 'width', '200')
  svgEl.setAttributeNS(svgNS, 'height', '200')
  svgEl.style.visibility = 'hidden'
  const textEl = document.createElementNS(svgNS, 'text')
  textEl.textContent = text
  svgEl.append(textEl)
  document.body.appendChild(svgEl)
  const bbox = textEl.getBBox()
  document.body.removeChild(svgEl)
  return bbox.width
}

// export type MultiChartLegendWidths = Record<'barChartDatasets' | 'lineChartDatasets', Record<string, number> | undefined>

// export const getLegendWidths = <T extends SVGDatumType>({
//   barChartDatasets,
//   lineChartDatasets,
// }: MultiChartLegendProps<T>): MultiChartLegendWidths => {
//   const output: MultiChartLegendWidths = {barChartDatasets: undefined, lineChartDatasets: undefined}
//   if( barChartDatasets ){
//     const barWidths: Record<string, number> = {}
//     for( const d of barChartDatasets ){
//       barWidths[d.key] = getSVGTextWidth(d.label)
//     }
//     output.barChartDatasets = barWidths
//   }
//   if( lineChartDatasets ){
//     const lineWidths: Record<string, number> = {}
//     for( const d of lineChartDatasets ){
//       lineWidths[d.key] = getSVGTextWidth(d.label)
//     }
//     output.lineChartDatasets = lineWidths
//   }
//   return output
// }

export type LegendConfiguration = {
  label: string
  x: number
  y: number
  width: number
  height: number
  dotSize: number
  dotMargin: number
  color: string
  type: 'bar' | 'line'
}

export const getLegendConfigs = <T extends SVGDatumType>({
  barChartDatasets,
  lineChartDatasets,
  width,
  xMargin = 12,
  yMargin = 10,
  dotMargin = 4,
  barDotSize = 10,
  lineDotSize = 12,
  legendItemHeight = 12,
}: (
  Pick<MultiChartProps<T>, 'barChartDatasets' | 'lineChartDatasets' | 'width'>
  & Partial<{
    xMargin: number
    yMargin: number
    dotMargin: number
    barDotSize: number
    lineDotSize: number
    legendItemHeight: number
  }>
)): LegendConfiguration[] => {
  let runningWidth = 0
  let y = 0
  const legendConfigs: LegendConfiguration[] = []
  if( barChartDatasets ){
    for( const d of barChartDatasets ){
      const textWidth = getSVGTextWidth(d.label)
      legendConfigs.push({
        x: runningWidth,
        y,
        width: textWidth,
        height: legendItemHeight,
        dotSize: barDotSize,
        dotMargin,
        label: d.label,
        color: d.color,
        type: 'bar',
      })
      const legendItemWidth = (textWidth + xMargin + barDotSize + dotMargin)
      if( (runningWidth + legendItemWidth) > width ){
        y += (legendItemHeight + yMargin)
        runningWidth = 0
      }else{
        runningWidth += legendItemWidth
      }
    }
  }
  if( lineChartDatasets ){
    for( const d of lineChartDatasets ){
      const textWidth = getSVGTextWidth(d.label)
      legendConfigs.push({
        x: runningWidth,
        y,
        width: textWidth,
        height: legendItemHeight,
        dotSize: lineDotSize,
        dotMargin,
        label: d.label,
        color: d.color,
        type: 'line',
      })
      const legendItemWidth = (textWidth + xMargin + lineDotSize + dotMargin)
      if( (runningWidth + legendItemWidth) > width ){
        y += (legendItemHeight + yMargin)
        runningWidth = 0
      }else{
        runningWidth += legendItemWidth
      }
    }
  }
  return legendConfigs
}

type MultiChartLegendProps = (
  {
    legends: LegendConfiguration[]
  }
  & SizeRequirements
  & Partial<Omit<React.SVGAttributes<SVGSVGElement>, 'width' | 'height'>>
)

export function MultiChartLegend({
  legends,
  ...props
}: MultiChartLegendProps): JSX.Element {
  return (
    <svg {...props}>
      {/* <rect fill='none' stroke='red' width={props.width} height={props.height} /> */}
      { legends.map( (legend, i) => (
        <g
          key={`legend-${i}`}
          transform={`translate(${legend.x},${legend.y})`}>
          { legend.type === 'line' ? (
            <g>
              <line
                stroke={legend.color}
                strokeWidth={2}
                x1={0}
                x2={legend.dotSize}
                y1={legend.height / 2}
                y2={legend.height / 2} />
              <circle
                cx={legend.dotSize / 2}
                cy={legend.height / 2}
                r={legend.dotSize / 4}
                fill={legend.color} />
            </g>
          ) : (
            <rect
              x={0}
              y={(legend.height - legend.dotSize) / 2}
              width={legend.dotSize}
              height={legend.dotSize}
              fill={legend.color} />
          )}
          <text
            x={legend.dotSize + legend.dotMargin}
            y={legend.height / 2}
            fontWeight={400}
            fontSize={12}
            fill='currentColor'
            dominantBaseline='central'>
            { legend.label }
          </text>
        </g>
      ))}
    </svg>
  )
}
